import { compareHelpers, stringHelpers } from '@approvalmax/utils';
import Fuse from 'fuse.js';
import { useMemo } from 'react';

import { fuseOptions } from './Content.constants';
import type { ContentProps } from './Content.types';

export const useSortedXeroBankAccounts = ({
    selectedBankAccount,
    xeroBankAccounts,
    newXeroAccountId,
    selectedBankAccountCurrencyCode,
    selectedBankAccountMatchedXeroBankAccountId,
}: {
    selectedBankAccount: ContentProps['bankAccounts'][number] | undefined;
    xeroBankAccounts: ContentProps['xeroBankAccounts'];
    newXeroAccountId: string | undefined;
    selectedBankAccountCurrencyCode: string | undefined;
    selectedBankAccountMatchedXeroBankAccountId: string | undefined;
}) => {
    return useMemo(() => {
        const fuseNickname = new Fuse([{ text: selectedBankAccount?.nickname ?? '' }], fuseOptions);

        return xeroBankAccounts
            .map((xeroBankAccount) => {
                // score weight described here https://approvalmax.atlassian.net/wiki/spaces/PRD/pages/2723840035/Bank+accounts#Mockups
                // within row 'Matching bank accounts to Xero bank accounts'
                let score = 0;

                if (stringHelpers.extractDigits(xeroBankAccount.sortCode) === selectedBankAccount?.sortCode || '') {
                    score += 3;
                }

                if (
                    stringHelpers.extractDigits(xeroBankAccount.bankAccountNumber) ===
                        selectedBankAccount?.bankAccountNumber ||
                    ''
                ) {
                    score += 2;
                }

                const nicknameMatch = fuseNickname.search(xeroBankAccount.name);

                // we might have one on no results here. When we have a result, we want to take the score of relevance
                // https://www.fusejs.io/api/options.html#includescore
                if (nicknameMatch.length) {
                    score += 1 - (nicknameMatch[0]?.score ?? 1);
                }

                const matchedWithAnotherBankAccount =
                    xeroBankAccount.matched && xeroBankAccount.id !== selectedBankAccountMatchedXeroBankAccountId;

                return {
                    ...xeroBankAccount,
                    // when Xero BA already matched w/ another BA, sort it to the list bottom but
                    // with higher priority than Xero BAs w/ another currency
                    score: matchedWithAnotherBankAccount ? -1 : score,
                };
            })
            .sort(compareHelpers.comparatorFor(compareHelpers.numberComparator2Desc, 'score'))
            .sort((a, b) => {
                // when creating new Xero bank account always place it on top
                if (newXeroAccountId) {
                    if (a.id === newXeroAccountId) {
                        return -1;
                    }
                }

                return compareHelpers.booleanComparator2Asc(
                    a.currencyCode === selectedBankAccountCurrencyCode,
                    b.currencyCode === selectedBankAccountCurrencyCode
                );
            });
    }, [
        selectedBankAccount?.nickname,
        selectedBankAccount?.sortCode,
        selectedBankAccount?.bankAccountNumber,
        xeroBankAccounts,
        newXeroAccountId,
        selectedBankAccountCurrencyCode,
        selectedBankAccountMatchedXeroBankAccountId,
    ]);
};
